<template>
  <teleport to="body">
    <transition name="ftour">
      <div
        v-if="active && current"
        class="ftour"
        role="dialog"
        aria-modal="true"
        :aria-label="`${pageTitle} 화면 안내`"
      >
        <button
          type="button"
          class="ftour-catch"
          :class="{ 'is-solid': !hole.ready }"
          aria-label="안내 닫기"
          @click="stopTour"
        />

        <div
          v-if="hole.ready"
          class="ftour-hole"
          :style="{
            top: `${hole.top}px`,
            left: `${hole.left}px`,
            width: `${hole.width}px`,
            height: `${hole.height}px`,
          }"
        />

        <div ref="cardEl" class="ftour-card" :class="{ 'is-center': !hole.ready }" :style="cardStyle">
          <p class="ftour-step">{{ stepIndex + 1 }} / {{ total }}</p>
          <h3 class="ftour-title">{{ current.name }}</h3>
          <p class="ftour-desc">{{ current.description }}</p>
          <div class="ftour-actions">
            <button type="button" class="ftour-ghost" @click="stopTour">건너뛰기</button>
            <div class="ftour-nav">
              <button type="button" class="ftour-btn" :disabled="isFirst" @click="prevStep">이전</button>
              <button type="button" class="ftour-btn is-primary" @click="nextStep">
                {{ isLast ? '완료' : '다음' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { BACK_LAYER, useBackCloseable } from '@/composables/useBackCloseable'
import { useFeatureTour } from '@/composables/useFeatureTour'

const route = useRoute()
const {
  active,
  current,
  stepIndex,
  total,
  isFirst,
  isLast,
  pageTitle,
  hole,
  nextStep,
  prevStep,
  stopTour,
  refreshHole,
} = useFeatureTour()

useBackCloseable(BACK_LAYER.featureTour, () => active.value, () => {
  stopTour()
})

const cardEl = ref<HTMLElement | null>(null)
const cardH = ref(220)

const measureCard = () => {
  const h = cardEl.value?.offsetHeight || 0
  if (h > 0) cardH.value = h
}

const cardStyle = computed(() => {
  if (!hole.value.ready || typeof window === 'undefined') return {}
  const gap = 12
  const pad = 12
  const vw = window.innerWidth
  const vh = window.innerHeight
  const cardW = Math.min(360, vw - 24)
  const h = cardH.value
  const holeTop = hole.value.top
  const holeBottom = hole.value.top + hole.value.height
  const spaceBelow = vh - holeBottom
  const spaceAbove = holeTop
  const holeIsLow = holeBottom > vh * 0.68
  const holeIsHigh = holeTop < vh * 0.22

  let placeBelow = spaceBelow >= h + gap
  if (holeIsLow && spaceAbove >= Math.min(h + gap, 96)) placeBelow = false
  else if (holeIsHigh && spaceBelow >= Math.min(h + gap, 96)) placeBelow = true
  else if (!placeBelow && spaceAbove < h + gap) placeBelow = spaceBelow >= spaceAbove

  let top = placeBelow ? holeBottom + gap : holeTop - h - gap
  top = Math.max(pad, Math.min(top, vh - h - pad))

  const overlaps = top < holeBottom - 4 && top + h > holeTop + 4
  if (overlaps) {
    if (spaceAbove >= spaceBelow) {
      top = Math.max(pad, holeTop - h - gap)
    } else {
      top = Math.min(holeBottom + gap, vh - h - pad)
    }
    top = Math.max(pad, Math.min(top, vh - h - pad))
  }

  let left = hole.value.left + hole.value.width / 2 - cardW / 2
  left = Math.max(pad, Math.min(left, vw - cardW - pad))

  return {
    top: `${top}px`,
    left: `${left}px`,
    width: `${cardW}px`,
  }
})

const onKey = (event: KeyboardEvent) => {
  if (!active.value) return
  if (event.key === 'Escape') stopTour()
  if (event.key === 'ArrowRight' || event.key === 'Enter') nextStep()
  if (event.key === 'ArrowLeft') prevStep()
}

watch(
  () => [current.value?.name, hole.value.ready, hole.value.top, hole.value.height],
  async () => {
    await nextTick()
    measureCard()
  }
)

watch(
  () => route.fullPath,
  (next, prev) => {
    if (!prev || next === prev) return
    if (active.value) stopTour()
  }
)

let cardObserver: ResizeObserver | null = null

onMounted(() => {
  window.addEventListener('resize', refreshHole)
  window.addEventListener('scroll', refreshHole, true)
  window.addEventListener('keydown', onKey)
  if (typeof ResizeObserver !== 'undefined') {
    cardObserver = new ResizeObserver(() => measureCard())
    watch(cardEl, (el, prev) => {
      if (prev) cardObserver?.unobserve(prev)
      if (el) {
        cardObserver?.observe(el)
        measureCard()
      }
    }, { immediate: true })
  }
})

onUnmounted(() => {
  window.removeEventListener('resize', refreshHole)
  window.removeEventListener('scroll', refreshHole, true)
  window.removeEventListener('keydown', onKey)
  cardObserver?.disconnect()
  cardObserver = null
  if (active.value) stopTour()
})
</script>
